import { GraphDataViewModel } from "@features/graph/graphViewModel"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { ProbabilityDensityChartOptions } from "@features/results/Result/ChartsContainer/ProbabilityDensityContainer"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import {
  ChartGroupLayout,
  DashboardThresholdChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import ThresholdChartContainer, {
  ThresholdChartContainerProps,
} from "@features/results/Result/ThresholdChartContainer"
import { GraphNodeData } from "@store/services/converter/types"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { useMemo, useRef } from "react"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { calculateLagShift } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

const chartOptions: ProbabilityDensityChartOptions = {
  isHistogramShown: true,
  isEstimationShown: false,
  isIQRangeVisible: false,
  isViolinDiagram: false,
  kernel: [],
  year: 0,
  secondYear: 0,
  sliderThresholdValue: 60,
  sliderEstimationBandwidthValue: 1,
  isSecondYear: false,
  isDemean: false,
  // min and max value are different for every case - need to be determined based on results data
  minVal: NaN,
  maxVal: NaN,

  clamp: false,
  clamp_stdev: 1,
  clamp_string: "Show area % within 1 st.dev",
  isCDF: false,
  showSuprenums: false,
}

type DashboardThresholdChartProps = {
  chart: DashboardThresholdChartDto
  layout?: ChartGroupLayout
} & Pick<ThresholdChartContainerProps, "run" | "graphData">

const DashboardThresholdChart = (props: DashboardThresholdChartProps) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const allNodes = Object.values(GraphDataViewModel.data!.nodeMap)
  const nodesData: GraphNodeData[] = selectedNodes
    .map((nodeLabel: string) => allNodes.find((node) => node.display.label === nodeLabel))
    .filter((n) => n !== undefined)

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys: [props.run.key],
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: selectedNodes.length === 0 || !isVisible,
    },
  )
  const runResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )
  const nodesResults = runResults?.[props.run.key] ?? {}

  const defaultGivenEventNodeLabel = props.chart.Default_given_event_node
    ? graphViewGroupNodeLabelToSampleNode(props.chart.Default_given_event_node)
    : undefined

  const defaultEventNodeLabel = props.chart.Default_event_node
    ? graphViewGroupNodeLabelToSampleNode(props.chart.Default_event_node)
    : undefined

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onFullScreen={handleFullscreen}
      onOptionChange={(v) => handleOptionChange(v.Nodes)}
      runIds={[props.run.key]}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isVisible && (
        <ThresholdChartContainer
          graphData={props.graphData}
          nodesData={nodesData}
          nodesResults={nodesResults}
          lag={lagShift}
          isLoading={isFetching}
          run={props.run}
          localStoragePrefix="dashboard"
          defaultGivenEventNodeLabel={defaultGivenEventNodeLabel}
          defaultEventNodeLabel={defaultEventNodeLabel}
          chartOptions={chartOptions}
          onlyChart
          maintainAspectRatio={false}
          showMinMaxBoundary={false}
          legendPosition={props.chart.LegendPosition}
          showLegend={cfg.showLegend}
          hiddenDatasets={cfg.hiddenDatasets}
          onLegendClick={handleLegendClick}
        />
      )}
    </DashboardChartContainer>
  )
}

export default DashboardThresholdChart
